import React, { Fragment } from "react";
import Page from "../components/page";
import { graphql } from "gatsby";
import List, {
  ListItem,
} from "@vorne/gatsby-theme-marketing/components/list/v1";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import {
  Body1,
  Heading1,
  Heading2,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import BannerAd from "@vorne/gatsby-theme-marketing/components/banner-ad/banner-ad";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import CallToActionButton from "@vorne/gatsby-theme-marketing/components/button/calltoaction/callToAction";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import Routes from "../routes";
import BigIdea from "@vorne/gatsby-theme-marketing/components/bigidea/bigIdea";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";
import { OEEBanner } from "@vorne/gatsby-theme-marketing/components/banners";

const AddressSixBigLosses = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>HOW TO ADDRESS THE SIX BIG LOSSES</PageTitle>
        <BigIdea>
          The true value and power of the Six Big Losses is in the path they
          provide to improving OEE. Each of the Six Big Losses has its own set
          of effective countermeasures.
        </BigIdea>
        <QuickLinks tag="h2" />
        <Heading1 id="overview">Overview</Heading1>
        <Body1>
          The <Link url={Routes.OEE.SixBigLosses}>Six Big Losses</Link> provide
          an equipment-based perspective on lost production time. Just like OEE,
          the Six Big Losses have their roots in TPM (Total Productive
          Maintenance). In fact, one of the goals of TPM is to eliminate the Six
          Big Losses.
        </Body1>
        <Body1>
          One of the most effective paths to improving OEE is through the Six
          Big Losses. Let’s spend a little time exploring ways to leverage the
          Six Big Losses as part of your improvement program.
        </Body1>
        <Link url={Routes.VorneWebsiteRoute}>
          <Image
            src={data.addressSixBigLossesImage}
            alt="Use Reason Codes to identify causes of the Six Big Losses"
            renderCaption={() => (
              <Fragment>
                Reason Codes provide additional detail to enhance the Six Big
                Losses by identifying why the process is not producing. In this
                example the process is experiencing an equipment failure because
                there is No Operator
              </Fragment>
            )}
          />
        </Link>
        <Heading1 id="equipmentFailure">Equipment Failure</Heading1>
        <Heading2>Overview</Heading2>
        <Body1>
          Addressing equipment failure (i.e., Unplanned Stops) is critical to{" "}
          <Link url={Routes.OEE.ImproveOEE}>improving OEE</Link>. For most
          companies, Unplanned Stop time is the single largest source of lost
          production time.
        </Body1>
        <Body1>
          A prerequisite to successfully addressing Unplanned Stops is to
          understand the nature of the problem. In other words, attribute each
          instance of an Unplanned Stop to a reason for the loss – through
          reason codes. This will enable you to apply Root Cause Analysis to
          your top losses.
        </Body1>
        <Heading2>Tips on Using Reason Codes</Heading2>
        <Body1>Here are some tips for effective use of reason codes:</Body1>
        <List type="bullet">
          <ListItem>Start simple, with no more than 25 reasons.</ListItem>
          <ListItem>Create a catch-all reason for ‘All Other Losses’.</ListItem>
          <ListItem>
            Make sure every reason is clear and unambiguous when compared with
            other reasons.
          </ListItem>
          <ListItem>
            Make sure every reason describes symptoms, as opposed to attempting
            to diagnose root causes.
          </ListItem>
          <ListItem>Remove reasons that aren't regularly used.</ListItem>
          <ListItem>
            Add reasons, as needed, to ensure that ‘All Other Losses’ is not in
            the top ten losses.
          </ListItem>
        </List>
        <Heading2>Focused Improvement</Heading2>
        <Body1>
          A highly effective process for reducing Unplanned Stop time is a
          focused improvement activity (sometimes called a kaizen blitz).
        </Body1>
        <Body1>
          In a focused improvement activity a cross-functional team selects one
          type of loss to address (often from a top losses report). The team
          then applies OEE loss analysis via a Root Cause Analysis or a 5 Why
          Analysis to identify potential causes and fixes.
        </Body1>
        <Body1>
          Once an issue is fixed, it is important to update related standardized
          work procedures to lock-in improvements (and avoid “same
          problem/different day” recurrences).
        </Body1>
        <OEEBanner />
        <Heading1 id="setupAndAdjustments">Setup and Adjustments</Heading1>
        <Body1>
          Another major source of lost production time for many manufacturers is
          setups (also called Changeovers or Make Ready). We recommend expanding
          the definition of Setup and Adjustments to cover all planned stops
          (e.g., including preventative maintenance) that occur during planned
          production time. After all, any time that could productively be used
          for manufacturing is an opportunity for improvement.
        </Body1>
        <Heading2>Establishing a Consistent Policy</Heading2>
        <Body1>
          A good starting point for Setup and Adjustments is to make sure you
          are consistent in how you measure Setup Time (to ensure you have an
          accurate information foundation). For example, your policy might be:
        </Body1>
        <List type="bullet">
          <ListItem>
            Setup Time is measured as the time between the last good part
            produced (before Setup) to the first good part produced (after
            Setup).
          </ListItem>
        </List>
        <Body1>
          Some companies prefer to include startup or warmup time as part of
          Setup, in which case your policy might be:
        </Body1>
        <List type="bullet">
          <ListItem>
            Setup Time is measured as the time between the last good part
            produced (before Setup) to the first instance of consistently
            producing parts that meet quality standards (after Setup).
          </ListItem>
        </List>
        <Body1>
          Either way is fine – just make sure to document your policy and apply
          it consistently.
        </Body1>
        <Heading2>SMED (Single-Minute Exchange of Die)</Heading2>
        <Body1>
          A well-established and highly effective way to address setup loss is
          SMED (Single-Minute Exchange of Die).
        </Body1>
        <Body1>
          SMED is a collection of techniques for dramatically reducing the time
          it takes to complete a setup. The goal of SMED is to reduce setup
          times to less than 10 minutes (i.e., single-digit minutes). Each
          element of the setup is analyzed to see if it can be:
        </Body1>
        <List type="bullet">
          <ListItem>
            Separated (moved, as is, before or after the setup)
          </ListItem>
          <ListItem>
            Converted (modified, so it can be moved before or after the setup)
          </ListItem>
          <ListItem>Streamlined (modified to be faster)</ListItem>
        </List>
        <Body1>
          Here are several examples of changes that are often made as part of a
          SMED program:
        </Body1>
        <List type="bullet">
          <ListItem>
            Put together setup carts with all tools and supplies necessary to
            complete the setup.
          </ListItem>
          <ListItem>
            Prepare parts in advance (e.g., preheat dies in advance of the
            setup).
          </ListItem>
          <ListItem>
            Use pinned or marked settings so that coarse equipment adjustments
            are no longer necessary.
          </ListItem>
          <ListItem>
            Eliminate bolts (e.g., use quick release mechanisms or other types
            of functional clamps).
          </ListItem>
        </List>
        <BannerAd url={Routes.VorneWebsiteRoute} />

        <Heading1 id="idlingAndMinorStopsReducedSpeed">
          Idling and Minor Stops/Reduced Speed
        </Heading1>
        <Body1>
          While it is fairly easy to manually calculate performance loss,
          breaking it down into the underlying{" "}
          <Link url={Routes.OEE.SixBigLosses}>Six Big Losses</Link> (Idling and
          Minor Stops and Reduced Speed) is considerably more difficult. For
          most manufacturing processes, accurate and detailed measurement
          requires an automated system that measures individual cycles.
        </Body1>
        <Body1>
          The root causes of Idling and Minor Stops and Reduced Speed are
          typically quite different, which is why they are independent elements
          of Six Big Loss analysis. This section does not make a distinction
          since many manufacturing operations do not have the capability of
          differentiating between Idling and Minor Stops (small stops) and
          Reduced Speed (slow cycles).
        </Body1>
        <Heading2>Ideal Cycle Time</Heading2>
        <Body1>
          A good starting point for addressing performance loss is to validate
          ideal cycle times (to ensure you have an accurate information
          foundation).
        </Body1>
        <Body1>
          Ideal cycle times should be available for every part and should
          represent the maximum theoretical speed of the equipment for that part
          (NOT ‘budget’ or ‘standard’ speeds that are slower than the maximum).
        </Body1>
        <Body1>
          If your OEE Performance is ever higher than 100%, carefully review the
          ideal cycle time. You are likely to find that it is too high (and
          masking lost production time).
        </Body1>
        <Heading2>Patterns</Heading2>
        <Body1>
          Look for patterns of significant performance loss (i.e., an unusual
          number of slow cycles or small stops). For example, performance loss
          may be more prevalent:
        </Body1>
        <List type="bullet">
          <ListItem>After a part or material change</ListItem>
          <ListItem>After an environmental change</ListItem>
          <ListItem>During a specific shift</ListItem>
          <ListItem>Over time (a consistent degradation over time)</ListItem>
        </List>
        <Body1>
          Once your team identifies a pattern apply Root Cause Analysis or 5 Why
          Analysis to identify potential causes and fixes.
        </Body1>
        <Body1>
          Once a problem is fixed, it is important to update related
          standardized work procedures to lock-in improvements (and to avoid
          “same problem/different day” recurrences).
        </Body1>
        <Body1>
          For high-speed equipment it may be worth purchasing a high-speed
          camera to capture cycles with enough resolution to see potential
          problems that would otherwise be invisible.
        </Body1>
        <Heading2>Countermeasures</Heading2>
        <Body1>Some common countermeasures for performance loss are:</Body1>
        <List type="bullet">
          <ListItem>
            Schedule proactive maintenance and lubrication activities.
          </ListItem>
          <ListItem>
            Introduce a 5S program to improve the condition of equipment.
          </ListItem>
          <ListItem>Train operators on standardized work procedures.</ListItem>
          <ListItem>Tighten material quality standards.</ListItem>
          <ListItem>Improve the precision of equipment set-points.</ListItem>
        </List>
        <Center $horizontally>
          <CallToActionButton url={Routes.OEE.Contact.LinkedIn}>
            Follow Us On LinkedIn
          </CallToActionButton>
        </Center>
        <Heading1 id="processDefectsReducedYield">
          Process Defects/Reduced Yield
        </Heading1>
        <Body1>
          Process Defects and Reduced Yield occur during different stages of
          production, which is why they are independent elements of the{" "}
          <Link url={Routes.OEE.SixBigLosses}>Six Big Losses</Link>:
        </Body1>
        <List type="bullet">
          <ListItem>
            Process Defects (also known as production rejects) occur during
            stable production.
          </ListItem>
          <ListItem>
            Reduced Yield (also known as startup rejects) occurs during startup
            and is often a result of setups and warmup periods.
          </ListItem>
        </List>
        <Body1>
          Note that the Six Big Losses capture lost time. They do not capture
          lost material when product is scrapped.
        </Body1>
        <Heading2>Reduce Variation</Heading2>
        <Body1>
          One of the most effective countermeasures to quality problems is to
          reduce variation (one of the central goals of Six Sigma). Variation is
          one of the reasons that there is often a higher rate of defects during
          startup – there is more variation in the process.
        </Body1>
        <Body1>
          A very effective way to reduce variation is to pay close attention to
          equipment settings and materials:
        </Body1>
        <List type="bullet">
          <ListItem>
            Equipment Settings (define and implement more precise equipment
            settings, in some cases on a part-by-part basis)
          </ListItem>
          <ListItem>
            Materials (run material tests on equipment to determine if
            tightening tolerances positively impacts quality)
          </ListItem>
        </List>
        <Body1>
          These same countermeasures are also often effective for performance
          loss (e.g., resulting in fewer jams).
        </Body1>
        <Heading1 id="SIC">SIC (Short Interval Control)</Heading1>
        <Body1>
          SIC (Short Interval Control) is a factory-floor process for driving
          production improvements during the shift. Each shift is split into
          short intervals of time (typically two to four hours), within which
          plant-floor employees use data to identify and implement improvement
          actions. These improvement actions may be countermeasures to ongoing
          or emerging problems, or they may be actions to improve existing
          production.
        </Body1>
        <Body1>
          SIC can be very effective for all types of losses, because it
          dramatically shortens the time between a problem occurring and a
          countermeasure being proposed.
        </Body1>
        <Heading1 id="theRoleOfTheSixBigLossesInTPM">
          The Role of the Six Big Losses in TPM
        </Heading1>
        <Body1>
          Even a fairly simple TPM (Total Productive Maintenance) program can
          have a significant positive impact on the{" "}
          <Link url={Routes.OEE.SixBigLosses}>Six Big Losses</Link>. Taking a
          “going back to basics” philosophy, when TPM was first introduced in
          Japan, many companies achieved significant improvements in OEE through
          various combinations of:
        </Body1>
        <List type="bullet">
          <ListItem>
            Measuring OEE and Six Big Losses: to track improvement
          </ListItem>
          <ListItem>
            Autonomous Maintenance: proactive maintenance by operators
          </ListItem>
          <ListItem>
            Preventive Maintenance: proactive maintenance by maintenance
            personnel
          </ListItem>
          <ListItem>
            Standardized Work: capturing and following best practices
          </ListItem>
          <ListItem>Error Proofing: quality by design</ListItem>
          <ListItem>Training: enhancing staff capability</ListItem>
        </List>
        <Center $horizontally>
          <CallToActionButton url={Routes.OEE.FreeTools}>
            Download Free OEE Tools
          </CallToActionButton>
        </Center>
      </Section>
    </Page>
  );
};

export default AddressSixBigLosses;

export function Head() {
  return (
    <Seo
      title="How to Address the Six Big Losses"
      description="One of the most effective paths to improving OEE is through the Six Big Losses. Learn how to address them here."
    />
  );
}

export const query = graphql`
  query {
    addressSixBigLossesImage: file(
      relativePath: { eq: "address-six-big-losses-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
  }
`;
